import React from 'react';
import { PageSEO } from '../PageSEO';
import { LegalPageLayout } from './LegalPageLayout';

/**
 * Privacy Policy — Phase 99A.2 light direction.
 *
 * Legal text preserved verbatim from the prior version. Only chrome
 * (navbar / hero / body wrapper / footer) has been brought into the
 * Phase 99A light public direction via LegalPageLayout.
 */
export const PrivacyPolicy = () => (
  <>
    <PageSEO
      title="Privacy Policy — GP Capital"
      description="How GP CAP Pty Ltd collects, uses, discloses and protects personal information through the GP Capital platform."
      path="/privacy"
      referrerPolicy="strict-origin"
    />
    <LegalPageLayout
      eyebrow="Privacy"
      titleLead="How we handle"
      titleAccent="personal information."
      lastUpdated="Last updated: 11 February 2026"
      testId="privacy-page"
      relatedLinks={[
        { to: '/terms', label: 'Terms of Service', testId: 'terms' },
        { to: '/platform-terms', label: 'Platform Terms', testId: 'platform-terms' },
        { to: '/disclosures', label: 'Disclosures', testId: 'disclosures' },
        { to: '/security', label: 'Security & Responsible Disclosure', testId: 'security' },
        { to: '/trust', label: 'Trust Center', testId: 'trust' },
      ]}
    >
      <p>
        This Privacy Policy explains how <strong>GP CAP Pty Ltd</strong> (ACN 665 155 608) operating as <strong>GP Capital</strong> (&quot;<strong>GP Capital</strong>&quot;, &quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot;, &quot;<strong>our</strong>&quot;) collects, uses, stores and discloses personal information when you visit our website, interact with our content, or communicate with us (the &quot;<strong>Services</strong>&quot;).
      </p>

      <h2>1) Scope</h2>
      <p>This Policy applies to personal information we handle through our Services, including information collected through:</p>
      <ul>
        <li>our website and related pages,</li>
        <li>email, phone, and other communications,</li>
        <li>relationship onboarding and due diligence (where applicable).</li>
      </ul>
      <p>It does not apply to third-party websites we link to.</p>

      <h2>2) Personal information we collect</h2>
      <p>We may collect:</p>
      <ul>
        <li><strong>Identifiers &amp; contact details:</strong> name, email, phone number, job title, organisation.</li>
        <li><strong>Professional and eligibility information you provide:</strong> investment preferences/mandates, jurisdiction, and any self-described eligibility status (e.g., institutional/wholesale/accredited), where relevant and lawful.</li>
        <li><strong>Communications:</strong> messages, submissions, and correspondence.</li>
        <li><strong>Technical &amp; usage data:</strong> IP address, device identifiers, browser type, pages viewed, referral URLs, session data.</li>
        <li><strong>Cookies and similar technologies:</strong> analytics and functionality tools (see Section 6).</li>
      </ul>

      <h2>3) How we collect personal information</h2>
      <p>We collect personal information:</p>
      <ul>
        <li><strong>directly from you</strong> (forms, emails, calls),</li>
        <li><strong>automatically</strong> (cookies, logs, analytics),</li>
        <li><strong>from service providers/third parties</strong> (e.g., hosting, security, CRM, verification/compliance) where relevant and lawful.</li>
      </ul>

      <h2>4) How we use personal information</h2>
      <p>We use personal information to:</p>
      <ul>
        <li>respond to inquiries and provide requested information,</li>
        <li>evaluate and manage relationships with counterparties and partners,</li>
        <li>conduct due diligence, compliance checks and risk management (where applicable),</li>
        <li>operate, secure, maintain and improve the Services,</li>
        <li>manage communications (including newsletters/updates if you opt in),</li>
        <li>detect and prevent fraud, misuse, and security incidents,</li>
        <li>comply with legal/regulatory obligations.</li>
      </ul>

      <h2>5) How we disclose personal information</h2>
      <p>We may disclose personal information to:</p>
      <ul>
        <li><strong>service providers</strong> (hosting, analytics, CRM, IT/security, communications) acting on our instructions,</li>
        <li><strong>professional advisers</strong> (legal, accounting, audit, compliance),</li>
        <li><strong>transaction counterparties</strong> where reasonably necessary to evaluate or execute opportunities and consistent with confidentiality expectations,</li>
        <li><strong>regulators/courts/authorities</strong> where required by law or to protect rights and safety,</li>
        <li><strong>successors</strong> in a corporate transaction (e.g., merger/reorganisation), subject to safeguards.</li>
      </ul>
      <p>We do not sell personal information in the ordinary course of business.</p>

      <h2>6) Cookies and analytics</h2>
      <p>
        We use cookies and similar technologies for functionality and analytics. You can control cookies through your browser settings and, where implemented, on-site cookie controls. Disabling cookies may impact site functionality.
      </p>

      <h2>7) Cross-border transfers</h2>
      <p>
        GP Capital operates across <strong>Australia and the United States</strong>. Your personal information may be processed or stored in multiple jurisdictions via our operations and service providers. We take reasonable steps to implement safeguards appropriate to the circumstances.
      </p>

      <h2>8) Security and retention</h2>
      <p>
        We maintain reasonable technical and organisational measures to protect personal information. No system is perfectly secure. We retain information only as long as necessary for the purposes described above, including legal, compliance and dispute-resolution needs.
      </p>

      <h2>9) Your rights and choices</h2>
      <p>
        Depending on your location and applicable law, you may request access to, correction of, or deletion of your personal information, and you may opt out of marketing communications at any time.
      </p>

      <h2>10) Children</h2>
      <p>Our Services are not directed to children and we do not knowingly collect personal information from children.</p>

      <h2>11) Updates</h2>
      <p>We may update this Policy by posting a revised version on this page and updating the &quot;Last updated&quot; date.</p>

      <h2>12) Contact</h2>
      <p>
        For privacy questions or requests: <a href="mailto:info@gp-cap.com">info@gp-cap.com</a>
      </p>
    </LegalPageLayout>
  </>
);

export default PrivacyPolicy;
